
import SelectArea from '@/plugins/selectArea'
import { createMap, LOCAL_AREA_SHAPE } from '@/utils'
import L, { LatLngBoundsLiteral } from 'leaflet'
import Vue from 'vue'

const CATALOG_STORED_KEY = 'catalogRequest'
const DEFAULT_NB_DAYS = 7
let catalogDefault = {
  starttime: new Date(new Date().getTime() - DEFAULT_NB_DAYS * 86400e3).toISOString().split('T')[0],
  endtime: new Date(new Date().getTime() + 86400e3).toISOString().split('T')[0],
  minlat: 41,
  maxlat: 45,
  minlon: 4,
  maxlon: 9.75,
  typeAll: true,
  typeEarthquake: false,
  typeQuarryBlast: false,
  typeExplosion: false,
  typeUndefined: false,
  mindepth: 0,
  maxdepth: 200,
  minmagnitude: -2,
  maxmagnitude: 10
}

export default Vue.extend({
  props: {
    noEventType: {
      type: Boolean,
      default: false
    },
    noOutputFormat: {
      type: Boolean,
      default: false
    },
    noEventId: {
      type: Boolean,
      default: false
    },
    showMap: {
      type: Boolean,
      default: true
    },
    defaultParams: {
      type: Object,
      default () {
        return {}
      }
    }
  },

  data () {
    catalogDefault = Object.assign(catalogDefault, this.defaultParams)
    return {
      useStored: false,
      map: null as L.Map | null,
      lastDaysItems: [
        { text: '-', value: -1 },
        { text: '7', value: 7 },
        { text: '15', value: 15 },
        { text: '30', value: 30 }
      ],
      eventid: null,
      eventidBtnDisabled: true,
      lastDays: DEFAULT_NB_DAYS,
      starttime: null as string | null,
      endtime: null as string | null,
      starttimeMenu: false,
      endtimeMenu: false,
      minlat: 0,
      maxlat: 0,
      minlon: 0,
      maxlon: 0,
      typeAll: true,
      typeEarthquake: false,
      typeQuarryBlast: false,
      typeExplosion: false,
      typeUndefined: false,
      depth: [0, 0],
      magnitude: [0, 0],
      output: 'web',
      selectArea: null as SelectArea | null
    }
  },

  mounted () {
    this.loadCatalogValues()
    if (this.map == null && this.showMap) {
      this.initMap()
    }
  },

  watch: {
    lastDays: function (value) {
      if (value > 0) {
        const now = new Date().getTime()
        this.starttime = new Date(now - value * 86400e3).toISOString().split('T')[0]
        this.endtime = new Date(now + 86400e3).toISOString().split('T')[0]
      }
    },
    showMap: function (value) {
      if (this.map == null && value) {
        this.initMap()
      }
    },
    defaultParams: function (value) {
      catalogDefault = Object.assign(catalogDefault, this.defaultParams)
      this.loadCatalogValues()
      this.updateSelectArea()
      if (this.map != null) {
        this.map.fitBounds(this.selectArea!.getBounds() as LatLngBoundsLiteral)
      }
    }
  },

  methods: {
    initMap () {
      const mapCanvas = this.$refs.mapCanvas
      if (mapCanvas != null) {
        this.map = createMap(mapCanvas)
        this.map.fitBounds(LOCAL_AREA_SHAPE)
        this.selectArea = new SelectArea([[this.minlat, this.minlon], [this.maxlat, this.maxlon]])
        this.selectArea.on('boundschange', (area) => {
          const bounds = area.getBounds()
          this.minlat = bounds[0][0]
          this.minlon = bounds[0][1]
          this.maxlat = bounds[1][0]
          this.maxlon = bounds[1][1]
        }).addTo(this.map)
      }
    },
    loadCatalogValues () {
      const jsonStored = localStorage.getItem(CATALOG_STORED_KEY)
      const values = Object.assign({}, catalogDefault)
      if (Object.keys(this.defaultParams).length > 0) {
        this.lastDays = -1
      } else if (jsonStored != null) {
        // overwrite default values with stored values
        this.useStored = true
        const stored = JSON.parse(jsonStored)
        this.lastDays = -1
        values.starttime = stored.start
        values.endtime = stored.end
        const spType = stored.eventtype != null ? stored.eventtype.split(',') : 'all'
        values.typeAll = spType.indexOf('all') >= 0
        values.typeEarthquake = spType.indexOf('earthquake') >= 0
        values.typeQuarryBlast = spType.indexOf('quarry blast') >= 0
        values.typeExplosion = spType.indexOf('explosion') >= 0
        values.typeUndefined = spType.indexOf('undefined') >= 0
        values.minlat = stored.minlat
        values.maxlat = stored.maxlat
        values.minlon = stored.minlon
        values.maxlon = stored.maxlon
        values.minmagnitude = stored.minmag
        values.maxmagnitude = stored.maxmag
        values.mindepth = stored.mindepth
        values.maxdepth = stored.maxdepth
      }
      this.starttime = values.starttime
      this.endtime = values.endtime
      this.typeAll = values.typeAll
      this.typeEarthquake = values.typeEarthquake
      this.typeQuarryBlast = values.typeQuarryBlast
      this.typeExplosion = values.typeExplosion
      this.typeUndefined = values.typeUndefined
      this.minlat = values.minlat
      this.maxlat = values.maxlat
      this.minlon = values.minlon
      this.maxlon = values.maxlon
      this.magnitude = [values.minmagnitude, values.maxmagnitude]
      this.depth = [values.mindepth, values.maxdepth]
    },
    resetDefault () {
      this.useStored = false
      this.starttime = catalogDefault.starttime
      this.endtime = catalogDefault.endtime
      this.typeAll = catalogDefault.typeAll
      this.typeEarthquake = catalogDefault.typeEarthquake
      this.typeQuarryBlast = catalogDefault.typeQuarryBlast
      this.typeExplosion = catalogDefault.typeExplosion
      this.typeUndefined = catalogDefault.typeUndefined
      this.minlat = catalogDefault.minlat
      this.maxlat = catalogDefault.maxlat
      this.minlon = catalogDefault.minlon
      this.maxlon = catalogDefault.maxlon
      this.magnitude = [catalogDefault.minmagnitude, catalogDefault.maxmagnitude]
      this.depth = [catalogDefault.mindepth, catalogDefault.maxdepth]
      this.lastDays = DEFAULT_NB_DAYS
      this.updateSelectArea()
      localStorage.removeItem(CATALOG_STORED_KEY)
    },
    updateSelectArea () {
      if (this.selectArea != null) {
        this.selectArea.setBounds([[this.minlat, this.minlon], [this.maxlat, this.maxlon]])
      }
    },
    handleTypeAllChange (value: boolean) {
      if (value) {
        this.typeEarthquake = this.typeQuarryBlast = this.typeExplosion = this.typeUndefined = false
      }
    },
    handleFormSubmit () {
      const args = {
        starttime: this.starttime,
        endtime: this.endtime,
        minlatitude: `${this.minlat}`,
        maxlatitude: `${this.maxlat}`,
        minlongitude: `${this.minlon}`,
        maxlongitude: `${this.maxlon}`,
        mindepth: `${this.depth[0]}`,
        maxdepth: `${this.depth[1]}`,
        minmagnitude: `${this.magnitude[0]}`,
        maxmagnitude: `${this.magnitude[1]}`,
        eventtype: [
          ['all', this.typeAll],
          ['earthquake', this.typeEarthquake],
          ['quarry blast', this.typeQuarryBlast],
          ['explosion', this.typeExplosion],
          ['other event', this.typeUndefined]
        ].filter(x => x[1]).map(x => x[0]).join(',') as string | undefined
      }
      if (this.noEventType) {
        delete args.eventtype
      }
      localStorage.setItem(CATALOG_STORED_KEY, JSON.stringify({
        start: args.starttime,
        end: args.endtime,
        minlat: args.minlatitude,
        maxlat: args.maxlatitude,
        minlon: args.minlongitude,
        maxlon: args.maxlongitude,
        mindepth: args.mindepth,
        maxdepth: args.maxdepth,
        minmag: args.minmagnitude,
        maxmag: args.maxmagnitude,
        eventtype: args.eventtype
      }))
      if (this.noOutputFormat) {
        this.$emit('input', args)
      } else {
        if (this.output === 'web') {
          this.$router.push({ name: 'catalogResult', query: args })
        } else {
          const query = Object.entries(Object.assign({ format: this.output }, args)).map(([k, v]) => `${k}=${v}`).join('&')
          location.assign(`fdsnws/event/1/query?${query}`)
        }
      }
    }
  }
})
